import React, { useMemo } from "react";
import { C, font, mono, btnD, lbl as lblStyle, inp as inpStyle } from "../../../../lib/styles.js";
import { JOB_ROLES } from "../../../../lib/constants.js";
import { seededLinkedContacts } from "../../../../lib/utils.js";

const ROLE_ORDER = ["contact", "producer", "vfx_supervisor", "vendor", "artist", "agency", "referral", "other"];

export default function LinkedContacts({ job, contacts, contactsSorted, clients, editing, onUpdate }) {
  const linkedClient = job.clientId ? (clients || []).find(cl => cl.id === job.clientId) : null;

  const contactsById = useMemo(() => {
    const m = new Map();
    (contacts || []).forEach(c => m.set(c.id, c));
    return m;
  }, [contacts]);

  const { grouped, sortedRoles } = useMemo(() => {
    // Prefer linkedContacts. Fall back to legacy linkedEntities for old jobs (read-only).
    const list = (job.linkedContacts || []).length > 0
      ? (job.linkedContacts || []).map((lc, i) => ({ ...lc, _idx: i }))
      : (job.linkedEntities || [])
          .filter(le => le.role !== "billed_client")
          .map((le, i) => ({ contactId: le.entityId, role: le.role || "contact", _idx: i, _legacy: true }));

    const g = {};
    list.forEach(lc => {
      const r = lc.role || "contact";
      if (!g[r]) g[r] = [];
      g[r].push(lc);
    });
    const roles = ROLE_ORDER.filter(r => g[r]);
    Object.keys(g).forEach(r => { if (!roles.includes(r)) roles.push(r); });
    return { grouped: g, sortedRoles: roles };
  }, [job.linkedContacts, job.linkedEntities]);

  const removeContact = (idx) => {
    const next = [...(job.linkedContacts || [])];
    next.splice(idx, 1);
    onUpdate({ linkedContacts: next });
  };

  const changeRole = (idx, role) => {
    const next = [...(job.linkedContacts || [])];
    next[idx] = { ...next[idx], role };
    onUpdate({ linkedContacts: next });
  };

  const addContact = (id) => {
    if (!id) return;
    // Seed from legacy linkedEntities on first write — otherwise creating
    // linkedContacts on a legacy-linked job hides its old links (the read
    // path above prefers a non-empty linkedContacts).
    const base = seededLinkedContacts(job);
    if (base.find(lc => lc.contactId === id)) return;
    onUpdate({ linkedContacts: [...base, { contactId: id, role: "contact" }] });
  };

  const anything = linkedClient || sortedRoles.length > 0;

  return (
    <div style={{ fontFamily: font }}>
      <div style={S.sectionHead}>
        <span style={lblStyle}>Linked Contacts</span>
      </div>

      {linkedClient && (
        <div style={{ marginBottom: 10 }}>
          <div style={S.roleHead}>Client</div>
          <div style={{ ...S.contactChip, borderLeftColor: `${C.teal}60` }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: C.teal, flexShrink: 0 }} />
            <span style={S.contactName}>{linkedClient.name}</span>
            {linkedClient.clientCode && (
              <span style={{ fontSize: 10.5, color: C.muted, fontFamily: mono }}>{linkedClient.clientCode}</span>
            )}
          </div>
        </div>
      )}

      {sortedRoles.map(role => (
        <div key={role} style={{ marginBottom: 10 }}>
          <div style={S.roleHead}>
            {JOB_ROLES[role] || role}
            {grouped[role].some(lc => lc._legacy) && <LegacyTag />}
          </div>
          {grouped[role].map(lc => {
            const ct = contactsById.get(lc.contactId);
            const isLegacy = !!lc._legacy;
            const displayName = ct?.name || (isLegacy ? "Legacy contact (unresolved)" : "Unknown");
            return (
              <div
                key={lc._idx + "-" + lc.contactId}
                style={{
                  ...S.contactChip,
                  borderLeftColor: isLegacy ? `${C.muted}50` : `${C.blue}60`,
                  opacity: isLegacy ? 0.7 : 1,
                }}
              >
                <span style={{
                  width: 6, height: 6, borderRadius: 3,
                  background: isLegacy ? C.muted : C.blue, flexShrink: 0,
                }} />
                <span style={{ ...S.contactName, color: isLegacy ? C.muted : C.text }}>{displayName}</span>
                {ct?.company && (
                  <span style={{ fontSize: 10.5, color: C.muted }}>{ct.company}</span>
                )}
                {editing && !isLegacy && (
                  <>
                    <select
                      style={S.roleSelect}
                      value={lc.role}
                      onChange={e => changeRole(lc._idx, e.target.value)}
                    >
                      {Object.entries(JOB_ROLES).filter(([k]) => k !== "billed_client").map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                    <button style={{ ...btnD, padding: "2px 7px", fontSize: 9.5 }} onClick={() => removeContact(lc._idx)}>×</button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {!anything && (
        <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 10 }}>No contacts linked</div>
      )}

      {editing && (
        <select
          style={{ ...inpStyle, fontSize: 11.5, padding: "6px 10px", cursor: "pointer" }}
          value=""
          onChange={e => addContact(e.target.value)}
        >
          <option value="">+ Link contact...</option>
          {contactsSorted.map(ct => (
            <option key={ct.id} value={ct.id}>
              {ct.name || "Unnamed"}{ct.company ? ` (${ct.company})` : ""}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function LegacyTag() {
  return (
    <span style={{
      marginLeft: 6, fontSize: 9, padding: "1px 6px", borderRadius: 3,
      background: `${C.muted}18`, color: C.muted,
      textTransform: "uppercase", letterSpacing: "0.08em",
    }}>Legacy</span>
  );
}

const S = {
  sectionHead: {
    display: "flex", alignItems: "center", gap: 12,
    marginBottom: 12, flexWrap: "wrap",
  },
  roleHead: {
    fontSize: 9.5, color: C.muted,
    textTransform: "uppercase", letterSpacing: "0.1em",
    marginBottom: 5,
    display: "inline-flex", alignItems: "center",
  },
  contactChip: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "7px 11px",
    borderLeft: `2px solid ${C.blue}60`,
    background: C.surfaceAlt,
    borderRadius: "0 6px 6px 0",
    marginBottom: 5,
  },
  contactName: {
    flex: 1, fontSize: 12.5, color: C.text,
    cursor: "pointer", minWidth: 0,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  roleSelect: {
    ...inpStyle, width: "auto", padding: "2px 22px 2px 8px", fontSize: 10,
    cursor: "pointer",
  },
};
